import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle
} from '@mui/material';
import { Close, Settings, Edit, Delete, Add } from '@mui/icons-material';
import { toast } from 'react-toastify';

const PARAM_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-parameters`;
const CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;

const FlowParameterDrawer = ({ open, onClose, flowId, flowName, mcId, mfId }) => {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  const [connectorOptions, setConnectorOptions] = useState([]);
  const [formData, setFormData] = useState({
    mfpName: '',
    mfpMcId: ''
  });
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState(null);

  // Fetch parameters when drawer opens or flow changes
  useEffect(() => {
    if (open && flowId) {
      console.log('Fetching parameters for flow:', flowId);
      fetchParameters();
      fetchConnectorOptions();
    }
  }, [open, flowId]);

  const fetchParameters = async () => {
    if (!flowId) return;
    
    setLoading(true);
    try {
      console.log(`Calling API: ${PARAM_API_URL}/flow/${flowId}`);
      const response = await fetch(`${PARAM_API_URL}/flow/${flowId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Parameters fetched:', data);
      setParameters(data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error(`Failed to load parameters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectorOptions = async () => {
    try {
      setLoading(true);
      const response = await fetch(CONNECTOR_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      console.log('Fetched connector options:', data); // Add logging
      setConnectorOptions(data);
      return data;
    } catch (error) {
      console.error('Error fetching connector options:', error);
      toast.error(`Error loading connector options: ${error.message}`);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const openFormDialog = async () => {
    resetForm();
    // Fetch connector options before opening the dialog
    await fetchConnectorOptions();
    setFormDialogOpen(true);
  };

  const closeFormDialog = () => {
    setFormDialogOpen(false);
  };

  const handleAddParameter = async () => {
    if (!formData.mfpName.trim()) {
      toast.warning('Parameter name cannot be empty');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        mfpName: formData.mfpName,
        mfpMfId: mfId, // Use the flow ID as mfpMfId
        mfpMcId: formData.mfpMcId ? formData.mfpMcId : null // Don't parse to integer for UUID
      };

      console.log('Adding parameter with payload:', payload);

      const response = await fetch(PARAM_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setParameters([...parameters, data]);
      resetForm();
      closeFormDialog();
      toast.success('Parameter added successfully');
    } catch (error) {
      console.error('Error adding parameter:', error);
      toast.error(`Failed to add parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditParameter = (parameter) => {
    setCurrentParameter(parameter);
    setFormData({
      mfpName: parameter.mfpName || '',
      mfpMcId: parameter.mfpMcId || '' // Handle potential undefined
    });
    setEditMode(true);
    setFormDialogOpen(true);
  };

  const handleUpdateParameter = async () => {
  if (!formData.mfpName.trim() || !currentParameter) {
    toast.warning('Parameter name cannot be empty');
    return;
  }

  setLoading(true);
  try {
    const payload = {
      mfpName: formData.mfpName,
      mfpMfId: mfId,
      mfpMcId: formData.mfpMcId || null,
    };

    const response = await fetch(`${PARAM_API_URL}/uuid/${currentParameter.mfpId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Refetch the parameters after a successful update
    await fetchParameters();

    resetForm();
    closeFormDialog();
    toast.success('Parameter updated successfully');
  } catch (error) {
    console.error('Error updating parameter:', error);
    toast.error(`Failed to update parameter: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  const handleDeleteParameter = (parameterId) => {
    const parameter = parameters.find((param) => param.mfpRecId === parameterId);
    if (!parameter) {
      toast.error('Parameter not found');
      return;
    }

    setParameterToDelete(parameter);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!parameterToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`${PARAM_API_URL}/uuid/${parameterToDelete.mfpId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refetch the parameters after a successful delete
      await fetchParameters();

      toast.success('Parameter deleted successfully');

      // Reset if we were editing this parameter
      if (currentParameter && currentParameter.mfpId === parameterToDelete.mfpId) {
        resetForm();
      }
    } catch (error) {
      console.error('Error deleting parameter:', error);
      toast.error(`Failed to delete parameter: ${error.message}`);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setParameterToDelete(null);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setCurrentParameter(null);
    setFormData({
      mfpName: '',
      mfpMcId: ''
    });
  };

  // Helper function to get connector name by ID
  const getConnectorNameById = (mcId) => {
    if (!mcId) return 'Not specified';
    const connector = connectorOptions.find(c => c.mcId === mcId);
    return connector ? connector.mcName : 'Unknown';
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 400 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <Settings sx={{ mr: 1 }} />
            <Typography variant="h6">Flow Parameters</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        
        <Typography variant="subtitle1" gutterBottom>
          Flow: {flowName || 'Unknown'}
        </Typography>
        
        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openFormDialog}
          >
            Add Parameter
          </Button>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Parameter List
        </Typography>
        
        {loading && !parameters.length ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : parameters.length === 0 ? (
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No parameters found for this flow
            </Typography>
          </Paper>
        ) : (
          <List sx={{ maxHeight: 500, overflow: 'auto' }}>
            {parameters.map((param) => (
              <React.Fragment key={param.mfpRecId}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleEditParameter(param)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDeleteParameter(param.mfpRecId)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText 
                    primary={param.mfpName} 
                    secondary={`Connector: ${getConnectorNameById(param.mfpMcId)}`}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Parameter Form Dialog */}
      <Dialog open={formDialogOpen} onClose={closeFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Parameter' : 'Add New Parameter'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Parameter Name"
              name="mfpName"
              value={formData.mfpName}
              onChange={handleInputChange}
              placeholder="Enter parameter name"
              disabled={loading}
              margin="normal"
              required
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="connector-select-label">Master Connector</InputLabel>
              <Select
                labelId="connector-select-label"
                id="connector-select"
                name="mfpMcId"
                value={formData.mfpMcId || ''}
                onChange={handleInputChange}
                label="Master Connector"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {connectorOptions.map((connector) => (
                  <MenuItem key={connector.mcId} value={connector.mcId || ''}>
                    {connector.mcName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={editMode ? "primary" : "success"}
            onClick={editMode ? handleUpdateParameter : handleAddParameter}
            disabled={!formData.mfpName.trim() || loading}
          >
            {loading ? <CircularProgress size={24} /> : editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Parameter</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to delete this parameter? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setParameterToDelete(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default FlowParameterDrawer;